import {
  TRAINING_VIDEO_FORMAT_LABEL,
  TRAINING_VIDEO_LIMITS,
  formatBytes,
  formatDuration,
  type TrainingExerciseDto,
  type TrainingVideoDto,
  type TrainingVideoSettings,
  type TrainingVideoUsageDto,
} from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { UploadAbortedError, uploadWithProgress, type UploadHandle } from '../../lib/upload';
import {
  VIDEO_FILE_ACCEPT,
  VIDEO_RECORDING_TIP,
  checkVideo,
  clock,
  readVideoMetadata,
  videoMime,
  videoSummary,
  type VideoCheck,
  type VideoMeta,
} from '../../lib/video';
import { Badge, Button, Field, Input, useToast } from '../ui';

/**
 * Videos einer Uebung: Wiedergabe fuer alle, Pflege (hochladen, umbenennen, ordnen,
 * loeschen) fuer die, die die Uebung bearbeiten duerfen. Aufgenommen wird am Handy
 * direkt in der Kamera oder aus einer Datei; vor dem Hochladen prueft der Browser
 * Format, Groesse und Laenge gegen die Einstellungen des Vereins.
 */

export interface VideoSettingsResponse {
  settings: TrainingVideoSettings;
  usage: TrainingVideoUsageDto;
}

export const VIDEO_SETTINGS_KEY = ['training-video-settings'];

/** Grenzen und Belegung des Vereins; Grundlage der Vorabpruefung. */
export function useVideoSettings(enabled = true) {
  return useQuery({
    queryKey: VIDEO_SETTINGS_KEY,
    queryFn: () => api.get<VideoSettingsResponse>('/api/settings/training-videos'),
    enabled,
  });
}

/* ------------------------------------------------------------------ *
 * Wiedergabe
 * ------------------------------------------------------------------ */

/**
 * Ein Video. `playsInline` haelt die Wiedergabe am iPhone in der Seite statt im Vollbild, `preload="metadata"`
 * laedt nur den Anfang (Mobilfunk). Kann der Browser das Format nicht (etwa HEVC in MOV am PC), bleibt ein Link.
 */
export function VideoPlayer({ video }: { video: TrainingVideoDto }): JSX.Element {
  return (
    <figure className="space-y-1">
      <video
        src={video.url}
        controls
        playsInline
        preload="metadata"
        aria-label={video.title || 'Trainingsvideo'}
        className="max-h-[60vh] w-full rounded-xl bg-black"
      >
        Dieser Browser kann das Video nicht abspielen. <a href={video.url} className="underline">Video öffnen</a>
      </video>
      {(video.title || video.durationSeconds) && (
        <figcaption className="text-sm text-slate-600">
          {video.title}
          {video.title && video.durationSeconds ? ' · ' : ''}
          {video.durationSeconds ? clock(video.durationSeconds) : ''}
        </figcaption>
      )}
    </figure>
  );
}

/** Die Videos zum Ansehen (Uebungsdetail, aufgeklappte Uebung einer Einheit). Ohne Videos nichts. */
export function ExerciseVideoList({ videos }: { videos: TrainingVideoDto[] }): JSX.Element | null {
  if (videos.length === 0) return null;
  return (
    <section aria-label="Videos" className="print:hidden">
      <h4 className="text-sm font-semibold text-slate-900">{videos.length === 1 ? 'Video' : 'Videos'}</h4>
      <div className="mt-1 space-y-3">
        {videos.map((video) => (
          <VideoPlayer key={video.id} video={video} />
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Pflege
 * ------------------------------------------------------------------ */

interface Pending {
  file: File;
  meta: VideoMeta | null;
  check: VideoCheck;
}

function UploadArea({ exercise, settings, usage }: { exercise: TrainingExerciseDto; settings: TrainingVideoSettings; usage: TrainingVideoUsageDto }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<Pending | null>(null);
  const [checking, setChecking] = useState(false);
  const [title, setTitle] = useState('');
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const handle = useRef<UploadHandle<TrainingVideoDto> | null>(null);

  // Wer den Dialog schliesst, bricht einen laufenden Upload ab.
  useEffect(() => () => handle.current?.abort(), []);

  const chosen = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setServerError(null);
    setPending(null);
    // Erst das, was ohne Auslesen feststeht: Format, Groesse, freier Speicher.
    const early = checkVideo(file, settings, usage, null);
    if (!early.ok) {
      setPending({ file, meta: null, check: early });
      return;
    }
    setChecking(true);
    const meta = await readVideoMetadata(file);
    setChecking(false);
    setPending({ file, meta, check: checkVideo(file, settings, usage, meta) });
  };

  const upload = async (): Promise<void> => {
    if (!pending?.check.ok) return;
    const { file, meta } = pending;
    const form = new FormData();
    // Felder zuerst, die Datei zuletzt: der Server liest die Datei als Strom und kennt nur, was davor kam.
    form.append('title', title.trim());
    if (meta) {
      form.append('durationSeconds', String(Math.round(meta.durationSeconds)));
      if (meta.width > 0) form.append('width', String(meta.width));
      if (meta.height > 0) form.append('height', String(meta.height));
    }
    const mime = videoMime(file);
    // Manche Geraete melden keinen Typ; der Server verlangt ihn.
    form.append('file', file.type === mime ? file : new File([file], file.name, { type: mime }), file.name);

    setServerError(null);
    setProgress({ loaded: 0, total: file.size });
    const started = uploadWithProgress<TrainingVideoDto>(`/api/training/exercises/${exercise.id}/videos`, form, (loaded, total) => setProgress({ loaded, total }));
    handle.current = started;
    try {
      await started.promise;
      setPending(null);
      setTitle('');
      toast.show('Video hochgeladen');
      await queryClient.invalidateQueries({ queryKey: ['training-exercise', exercise.id] });
      await queryClient.invalidateQueries({ queryKey: ['training-exercises'] });
      await queryClient.invalidateQueries({ queryKey: VIDEO_SETTINGS_KEY });
    } catch (error) {
      if (error instanceof UploadAbortedError) toast.show('Hochladen abgebrochen');
      else setServerError(error instanceof ApiError ? error.message : 'Das Hochladen ist fehlgeschlagen.');
    } finally {
      handle.current = null;
      setProgress(null);
    }
  };

  const free = Math.max(0, usage.quotaBytes - usage.usedBytes);
  const uploading = progress !== null;
  const percent = progress && progress.total > 0 ? Math.min(100, Math.round((progress.loaded / progress.total) * 100)) : 0;
  const pick = 'inline-flex min-h-11 cursor-pointer items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-within:outline focus-within:outline-2 focus-within:outline-brand-500 sm:min-h-0';

  return (
    <div className="space-y-3 rounded-xl border border-dashed border-slate-300 p-3">
      <p className="text-sm text-slate-600">
        {TRAINING_VIDEO_FORMAT_LABEL} · bis {settings.maxSizeMb} MB und {formatDuration(settings.maxDurationSeconds)} · noch {formatBytes(free)} frei
      </p>

      {!uploading && (
        <div className="flex flex-wrap gap-2">
          {/* Am Handy startet "aufnehmen" die Kamera, am PC öffnet es die Dateiauswahl. */}
          <label className={pick}>
            Video aufnehmen
            <input type="file" accept="video/*" capture="environment" className="sr-only" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; void chosen(file); }} />
          </label>
          <label className={pick}>
            Datei auswählen
            <input type="file" accept={VIDEO_FILE_ACCEPT} className="sr-only" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; void chosen(file); }} />
          </label>
        </div>
      )}

      {checking && <p className="text-sm text-slate-500" role="status">Video wird geprüft …</p>}

      {pending && !pending.check.ok && !uploading && (
        <div role="alert" className="space-y-1 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
          <p>{pending.check.message}</p>
          <p className="text-rose-700">{VIDEO_RECORDING_TIP}</p>
        </div>
      )}

      {pending?.check.ok && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-slate-800">{videoSummary(pending.file, pending.meta)}</p>
          {!pending.meta && (
            <p className="text-sm text-amber-800">Die Länge ließ sich nicht auslesen und wird deshalb nicht vorab geprüft – Format und Größe passen.</p>
          )}
          {pending.meta && pending.meta.height > 1080 && (
            <p className="text-sm text-slate-600">Hinweis: 720p genügt für Trainingsvideos und spart Platz und Datenvolumen.</p>
          )}
          <Field label="Titel (optional)">
            {(id) => <Input id={id} value={title} maxLength={TRAINING_VIDEO_LIMITS.title} disabled={uploading} onChange={(event) => setTitle(event.target.value)} placeholder="z. B. Ablauf von der Seite" />}
          </Field>
        </div>
      )}

      {uploading && progress && (
        <div className="space-y-2" aria-live="polite">
          <progress className="h-3 w-full" max={100} value={percent} aria-label="Fortschritt beim Hochladen" />
          <p className="text-sm text-slate-700">
            {percent < 100 ? `${percent} % · ${formatBytes(progress.loaded)} von ${formatBytes(progress.total)}` : 'Wird gespeichert …'}
          </p>
          <Button type="button" variant="secondary" size="sm" onClick={() => handle.current?.abort()}>Abbrechen</Button>
        </div>
      )}

      {serverError && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{serverError}</p>}

      {pending?.check.ok && !uploading && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => void upload()}>Hochladen</Button>
          <Button type="button" variant="secondary" onClick={() => { setPending(null); setServerError(null); }}>Verwerfen</Button>
        </div>
      )}
    </div>
  );
}

function ManagedVideo({ video, index, count, order, exerciseId }: { video: TrainingVideoDto; index: number; count: number; order: string[]; exerciseId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(video.title);
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['training-exercise', exerciseId] });
    await queryClient.invalidateQueries({ queryKey: ['training-exercises'] });
    await queryClient.invalidateQueries({ queryKey: VIDEO_SETTINGS_KEY });
  };
  const fail = (fallback: string) => (error: unknown) => toast.show(error instanceof ApiError ? error.message : fallback, 'error');

  const rename = useMutation({
    mutationFn: () => api.patch(`/api/training/videos/${video.id}`, { title: title.trim() }),
    onSuccess: async () => { setEditing(false); await refresh(); toast.show('Titel gespeichert'); },
    onError: fail('Der Titel konnte nicht gespeichert werden'),
  });
  const move = useMutation({
    mutationFn: (direction: -1 | 1) => {
      const next = [...order];
      const [moved] = next.splice(index, 1);
      next.splice(index + direction, 0, moved as string);
      return api.put(`/api/training/exercises/${exerciseId}/videos/order`, { ids: next });
    },
    onSuccess: refresh,
    onError: fail('Die Reihenfolge konnte nicht geändert werden'),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/api/training/videos/${video.id}`),
    onSuccess: async () => { await refresh(); toast.show('Video gelöscht'); },
    onError: fail('Das Video konnte nicht gelöscht werden'),
  });

  const name = video.title || `Video ${index + 1}`;
  return (
    <li className="space-y-2 rounded-xl border border-slate-200 p-3">
      <VideoPlayer video={{ ...video, title: '' }} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 break-words text-sm font-medium text-slate-900">{name}</span>
        <Badge tone="neutral">{formatBytes(video.sizeBytes)}</Badge>
      </div>
      {editing ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-0 flex-1">
            <Field label="Titel">{(id) => <Input id={id} value={title} maxLength={TRAINING_VIDEO_LIMITS.title} onChange={(event) => setTitle(event.target.value)} />}</Field>
          </div>
          <Button size="sm" loading={rename.isPending} onClick={() => rename.mutate()}>Speichern</Button>
          <Button size="sm" variant="secondary" onClick={() => { setEditing(false); setTitle(video.title); }}>Abbrechen</Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)} aria-label={`${name} umbenennen`}>Umbenennen</Button>
          <Button size="sm" variant="secondary" disabled={index === 0} loading={move.isPending} onClick={() => move.mutate(-1)} aria-label={`${name} nach oben`}>Nach oben</Button>
          <Button size="sm" variant="secondary" disabled={index === count - 1} loading={move.isPending} onClick={() => move.mutate(1)} aria-label={`${name} nach unten`}>Nach unten</Button>
          <Button
            size="sm"
            variant="secondary"
            loading={remove.isPending}
            aria-label={`${name} löschen`}
            onClick={() => { if (window.confirm(`„${name}“ endgültig löschen? Die Datei wird entfernt.`)) remove.mutate(); }}
          >
            Löschen
          </Button>
        </div>
      )}
    </li>
  );
}

/** Videos pflegen: bis zu drei je eigener Uebung. */
export function ExerciseVideoManager({ exercise }: { exercise: TrainingExerciseDto }): JSX.Element {
  const settings = useVideoSettings();
  const videos = exercise.videos;
  const order = videos.map((video) => video.id);
  const full = videos.length >= TRAINING_VIDEO_LIMITS.perExercise;

  return (
    <section aria-label="Videos pflegen" className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-slate-900">Videos ({videos.length} von {TRAINING_VIDEO_LIMITS.perExercise})</h4>
        <p className="hint">Eigene Videos zeigen die Übung in der Praxis. Eine Kopie der Übung bekommt die Videos nicht mit.</p>
      </div>
      {videos.length > 0 && (
        <ul className="space-y-3">
          {videos.map((video, index) => (
            <ManagedVideo key={video.id} video={video} index={index} count={videos.length} order={order} exerciseId={exercise.id} />
          ))}
        </ul>
      )}
      {full ? (
        <p className="text-sm text-slate-600">Mehr als {TRAINING_VIDEO_LIMITS.perExercise} Videos sind nicht möglich – bitte zuerst eines löschen.</p>
      ) : settings.data ? (
        <UploadArea exercise={exercise} settings={settings.data.settings} usage={settings.data.usage} />
      ) : (
        <p className="text-sm text-slate-500">Grenzen werden geladen …</p>
      )}
    </section>
  );
}
